import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Loading from "../common/Loading";
import { PrimaryButton } from "./shared";
import { trackApi, trackErrorMessage } from "./trackApi";

// Artist Studio > Track detail > Promotion. "Promote this sound" opens the existing Ads wizard with this track chosen
// (the server decides who may promote and sends the link), and the card lists this track's campaigns with their status and
// results. Everything is read from one call, GET /v1/artist-track/promotion. The Ads screens themselves are not changed here.

const TONE = {
  Active: "bg-green-100 text-green-800",
  Approved: "bg-blue-100 text-blue-800",
  "In review": "bg-amber-100 text-amber-800",
  Draft: "bg-gray-100 text-gray-700",
  Paused: "bg-gray-100 text-gray-700",
  Completed: "bg-gray-100 text-gray-700",
  Rejected: "bg-red-100 text-red-800",
  Suspended: "bg-red-100 text-red-800",
};

const number = (n) => Number(n || 0).toLocaleString();
const date = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};
export const money = (value, currency) => {
  const n = Number(value) || 0;
  try {
    return currency
      ? new Intl.NumberFormat(undefined, { style: "currency", currency, minimumFractionDigits: 2 }).format(n)
      : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } catch (e) {
    return n.toFixed(2);
  }
};

export default function TrackPromotion({ trackId }) {
  const [state, setState] = useState({ status: "loading", data: null, error: "" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ status: "loading", data: s.data, error: "" }));
    trackApi.promotion(trackId).then(
      (data) => { if (!cancelled) setState({ status: "ready", data, error: "" }); },
      (e) => { if (!cancelled) setState({ status: "error", data: null, error: trackErrorMessage(e, "We could not load this track's promotion. Please try again.") }); }
    );
    return () => { cancelled = true; };
  }, [trackId, attempt]);

  const { data } = state;
  const campaigns = (data && data.campaigns) || [];

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-6 mt-6" aria-label="Promotion" data-testid="track-promotion">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Promotion</h3>
          <p className="text-sm text-gray-500 mt-1">Reach more listeners with a paid ad for this sound.</p>
        </div>
        {data && data.can_promote && (
          <Link to={data.promote_path} data-testid="promote-button">
            <PrimaryButton>Promote this sound</PrimaryButton>
          </Link>
        )}
        {data && !data.can_promote && (
          <PrimaryButton disabled data-testid="promote-button-disabled" aria-describedby="promote-reason">Promote this sound</PrimaryButton>
        )}
      </div>

      {data && !data.can_promote && data.reason && (
        <p id="promote-reason" className="mt-3 text-sm text-gray-600" data-testid="promote-reason">{data.reason}</p>
      )}

      {state.status === "error" && (
        <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert" data-testid="promotion-error">
          {state.error} <button type="button" className="underline font-semibold" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
        </p>
      )}
      {state.status === "loading" && !data && <div className="flex justify-center py-8"><Loading /></div>}

      {data && campaigns.length === 0 && (
        <p className="mt-4 rounded-xl bg-gray-50 px-4 py-6 text-center text-sm text-gray-600" data-testid="promotion-empty">
          No campaigns for this sound yet.
        </p>
      )}

      {campaigns.length > 0 && (
        <ul className="mt-4 space-y-3" data-testid="promotion-list">
          {campaigns.map((c) => (
            <li key={c.campaign_id} className="rounded-xl border border-gray-200 p-4" data-testid={`campaign-${c.campaign_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold text-gray-900 break-words">{c.name}</p>
                <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${TONE[c.status] || "bg-gray-100 text-gray-700"}`} data-testid={`campaign-status-${c.campaign_id}`}>
                  {c.status}
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                {date(c.start_date)} – {c.end_date ? date(c.end_date) : "no end date"} · Budget {money(c.budget, data.currency)}{c.budget_type === "daily" ? " per day" : " in total"}
              </p>
              <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div><dt className="text-gray-500">Spent</dt><dd className="font-semibold">{money(c.spend, data.currency)}</dd></div>
                <div><dt className="text-gray-500">Impressions</dt><dd className="font-semibold">{number(c.impressions)}</dd></div>
                <div><dt className="text-gray-500">Clicks</dt><dd className="font-semibold">{number(c.clicks)}</dd></div>
                <div><dt className="text-gray-500">Plays from the ad</dt><dd className="font-semibold">{number(c.promoted_plays)}</dd></div>
              </dl>
              {c.status === "Rejected" && (
                <p className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900" role="alert" data-testid={`campaign-reason-${c.campaign_id}`}>
                  <span className="font-semibold">Not approved:</span> {c.rejection_reason || "The ad did not meet the advertising policy."}
                </p>
              )}
              <Link to={c.edit_path} className="inline-block mt-3 text-sm font-semibold underline" data-testid={`campaign-link-${c.campaign_id}`}>
                {c.status === "Rejected" ? "Edit and resubmit" : "View campaign"}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
